import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
} from 'react';
import {
  trackWeatherForecastChartScrubEnded,
  trackWeatherForecastChartScrubStarted,
  type WeatherForecastChartName,
} from '../../helpers/analytics/weatherForecastAnalytics';
import { useWeatherForecastDayGraphTime } from './useDayGraphTime';

type DayGraphTimeState = ReturnType<typeof useWeatherForecastDayGraphTime>;

const GraphTimeContext = createContext<DayGraphTimeState | null>(null);

export interface GraphTimeProviderProps {
  selectedDate: string;
  timeZone: string | undefined;
  isToday: boolean;
  /** When false, skip refreshing default scrub time (inactive swipe pane). */
  isActive?: boolean;
  children: ReactNode;
}

export const GraphTimeProvider: FC<GraphTimeProviderProps> = ({
  selectedDate,
  timeZone,
  isToday,
  isActive = true,
  children,
}) => {
  const graphTime = useWeatherForecastDayGraphTime(
    selectedDate,
    timeZone,
    isToday,
    isActive,
  );

  return (
    <GraphTimeContext.Provider value={graphTime}>
      {children}
    </GraphTimeContext.Provider>
  );
};

export const useGraphTime = (chart?: WeatherForecastChartName) => {
  const context = useContext(GraphTimeContext);

  if (!context) {
    throw new Error('useGraphTime must be used within GraphTimeProvider');
  }

  const handleSelectedChartTimeChange = useCallback(
    (time: Date, _index: number) => {
      context.handleSelectedChartTimeChange(time);
    },
    [context],
  );

  const handleChartScrubStart = useCallback(() => {
    if (chart) {
      trackWeatherForecastChartScrubStarted(chart);
    }
  }, [chart]);

  const handleChartScrubEnd = useCallback(() => {
    if (chart) {
      trackWeatherForecastChartScrubEnded(chart);
    }
  }, [chart]);

  return {
    selectedGraphTime: context.selectedGraphTime,
    selectedChartTime: context.selectedChartTime,
    defaultChartTime: context.defaultChartTime,
    handleSelectedChartTimeChange,
    handleChartScrubStart,
    handleChartScrubEnd,
  };
};
